/**
 * WaveSystem.ts
 * Spawns waves of enemy units at the map edges and advances wave state.
 *
 * Stage 5/6/7: hardcoded onboarding waves exercising all five enemy types.
 * Stage 7+: load waves from LevelDefinition.waves (ContentRepository / Supabase).
 *
 * Enemy types for this stage
 *   StreamingMogul — slow and tanky; relentless advance
 *   GenZSwarm      — fast and fragile; rush the beacon in numbers
 *   BadManager     — infiltrator; hunts Ali / promoter units
 *   TributeAct     — slow tank; sustained direct pressure
 *   MusicPress     — neutral timed threat; turns hostile if ignored
 *   FilePirate     — ranged harasser; throws torrent packets at venues/fans
 *   RivalVenueBuilder — non-combat builder; creates persistent enemy venues
 */

import { Unit }            from '../entities/Unit';
import { Faction }         from '../types';
import type { BalanceValues, EnemyType, GridPosition, SpawnEdge } from '../types';
import type { TileMap }    from '../map/TileMap';
import type { UnitRenderer } from '../rendering/UnitRenderer';
import {
  MUSIC_PRESS_HOSTILE_TIME,
  WAVE_INFINITE_INTERVAL,
  MOGUL_AURA_INTERVAL,
  GENZ_BURST_INTERVAL,
  CORPORATE_HQ_EDGE_SPAWN_EXCLUSION_RADIUS,
  BUILD_SCORE_ESCALATION_DELAY,
  BUILD_SCORE_ESCALATION_DIV,
  BUILD_SCORE_ESCALATION_CAP,
  ENEMY_DAMAGE_RIVAL_BUILDER,
  ENEMY_HP_RIVAL_BUILDER,
  ENEMY_MOVE_SPEED_RIVAL_BUILDER,
} from '../constants';

type EnemyVariant = 'normal' | 'earlyScout' | 'streamingMonolith' | 'monolithGuard' | 'signalJammer';

interface EnemySpec { type: EnemyType; edge: SpawnEdge; variant?: EnemyVariant; }
interface WaveSpec  { delay: number;  enemies: EnemySpec[]; notice?: string; }

export interface EnemyIntroduction {
  type: EnemyType;
  key: string;
  title: string;
  disruption: string;
  severity: 'info' | 'warning' | 'danger';
}

// ── Stage 5 onboarding waves ───────────────────────────────────────────
// The opening is intentionally quiet so new players can move, scout, claim
// venues, mine Gear, and build at least one fan workshop before pressure
// becomes dangerous. The first two waves are warnings more than kill checks.
const FIRST_WAVE_REPUTATION_TRIGGER = 90;
const PROPHECY_WARNING_SECS = 25;
const SPAWN_EDGES: SpawnEdge[] = ['north', 'east', 'south', 'west'];
const EDGE_TOO_CLOSE_TO_ARMY = 12;
const PROBE_JITTER_BEFORE = 4;
const PROBE_JITTER_AFTER = 8;
const PROBE_MIN_GAP = 13;
const SCRIPTED_JITTER_BEFORE = 18;
const SCRIPTED_JITTER_AFTER = 32;
const SCRIPTED_MIN_GAP = 38;
const INFINITE_INTERVAL_JITTER = 18;
const EARLY_PROBES: WaveSpec[] = [
  {
    delay: 12,
    notice: 'A&R has been spotted at the edge of town',
    enemies: [
      { type: 'GenZSwarm', edge: 'north', variant: 'earlyScout' },
    ],
  },
  {
    delay: 32,
    notice: 'A&R is testing a different route',
    enemies: [
      { type: 'GenZSwarm', edge: 'south', variant: 'earlyScout' },
    ],
  },
  {
    delay: 55,
    notice: 'Industry is sending A&R toward the venue routes',
    enemies: [
      { type: 'GenZSwarm', edge: 'west', variant: 'earlyScout' },
      { type: 'GenZSwarm', edge: 'east', variant: 'earlyScout' },
    ],
  },
  {
    delay: 80,
    notice: 'A&R pressure is building before the first real wave',
    enemies: [
      { type: 'GenZSwarm', edge: 'north', variant: 'earlyScout' },
      { type: 'GenZSwarm', edge: 'west', variant: 'earlyScout' },
      { type: 'GenZSwarm', edge: 'east', variant: 'earlyScout' },
    ],
  },
];
const STAGE5_WAVES: WaveSpec[] = [
  {
    delay: 90,
    enemies: [
      { type: 'GenZSwarm', edge: 'north' },
      { type: 'GenZSwarm', edge: 'south' },
    ],
  },
  {
    delay: 135,
    enemies: [
      { type: 'GenZSwarm',  edge: 'west' },
      { type: 'GenZSwarm',  edge: 'south' },
      { type: 'MusicPress', edge: 'east' },
      { type: 'FilePirate', edge: 'north' },
    ],
  },
  {
    delay: 215,
    enemies: [
      { type: 'GenZSwarm',      edge: 'north' },
      { type: 'GenZSwarm',      edge: 'east'  },
      { type: 'StreamingMogul', edge: 'north', variant: 'streamingMonolith' },
      { type: 'GenZSwarm',      edge: 'north', variant: 'monolithGuard' },
      { type: 'GenZSwarm',      edge: 'north', variant: 'monolithGuard' },
      { type: 'GenZSwarm',      edge: 'north', variant: 'monolithGuard' },
      { type: 'GenZSwarm',      edge: 'north', variant: 'monolithGuard' },
      { type: 'GenZSwarm',      edge: 'north', variant: 'monolithGuard' },
      { type: 'GenZSwarm',      edge: 'north', variant: 'monolithGuard' },
      { type: 'GenZSwarm',      edge: 'east',  variant: 'monolithGuard' },
      { type: 'BadManager',     edge: 'east',  variant: 'signalJammer' },
    ],
  },
  {
    delay: 300,
    enemies: [
      { type: 'BadManager',     edge: 'west'  },
      { type: 'MusicPress',     edge: 'north' },
      { type: 'StreamingMogul', edge: 'east'  },
      { type: 'BadManager',     edge: 'south', variant: 'signalJammer' },
      { type: 'FilePirate',     edge: 'north' },
      { type: 'GenZSwarm',      edge: 'east'  },
      { type: 'GenZSwarm',      edge: 'west'  },
      { type: 'GenZSwarm',      edge: 'south' },
    ],
  },
  {
    delay: 400,
    enemies: [
      { type: 'TributeAct',     edge: 'south' },
      { type: 'BadManager',     edge: 'west'  },
      { type: 'StreamingMogul', edge: 'east'  },
      { type: 'GenZSwarm',      edge: 'north' },
      { type: 'GenZSwarm',      edge: 'south' },
      { type: 'MusicPress',     edge: 'east'  },
      { type: 'BadManager',     edge: 'west', variant: 'signalJammer' },
      { type: 'FilePirate',     edge: 'north' },
      { type: 'GenZSwarm',      edge: 'east' },
    ],
  },
];

export class WaveSystem {
  private elapsed    = 0;
  private waveIndex  = 0;
  /** Increments each spawn so consecutive units land on different edge tiles. */
  private spawnSeed  = 0;
  /** How many infinite waves (post-onboarding) have fired. Drives difficulty scaling. */
  private infiniteWaveCount = 0;
  /** Build score passed from Game.ts each tick; used to gently scale post-grace-period waves. */
  private buildScore = 0;
  /** Elapsed time when the last infinite wave was sent. */
  private lastInfiniteWaveTime = 0;
  private probeIndex = 0;
  private lastAttackEdge: SpawnEdge | null = null;
  /** Last STAGE5_WAVES index that received a Prophecy warning (0 = skip wave 0). */
  private warnedWaveIndex = 0;
  /** Whether a Prophecy warning has fired for the next incoming infinite wave. */
  private infiniteWaveWarned = false;
  /** Per-run schedule variance. Saved so reloads keep the same incoming threats. */
  private probeSchedule: WaveSpec[] = [];
  private scriptedSchedule: WaveSpec[] = [];
  private nextInfiniteInterval = WAVE_INFINITE_INTERVAL;
  /** One-shot enemy-class reveals, drained by Game.ts for arrival toasts. */
  readonly pendingEnemyIntroductions: EnemyIntroduction[] = [];
  readonly pendingEnemyActivity: Array<{ message: string; severity: 'info' | 'warning' }> = [];
  private readonly introducedEnemyTypes = new Set<string>();

  /** Pre-computed passable edge tiles per direction (built once on init). */
  private readonly edgeTiles: Record<SpawnEdge, Array<{col: number; row: number}>> = {
    north: [], south: [], east: [], west: [],
  };
  /** Passable tiles connected to the main Beacon landmass. Generic waves must spawn here. */
  private readonly mainReachableTiles = new Set<string>();

  constructor(
    private readonly map:      TileMap,
    private readonly renderer: UnitRenderer,
    private readonly units:    Unit[],
    private readonly balance:  BalanceValues,
  ) {
    this.buildMainReachableTiles();
    this.buildEdgeTiles();
    this.probeSchedule = randomiseWaveSchedule(
      EARLY_PROBES,
      PROBE_JITTER_BEFORE,
      PROBE_JITTER_AFTER,
      PROBE_MIN_GAP,
    );
    this.scriptedSchedule = randomiseWaveSchedule(
      STAGE5_WAVES.map((wave, index) => varyScriptedWave(wave, index)),
      SCRIPTED_JITTER_BEFORE,
      SCRIPTED_JITTER_AFTER,
      SCRIPTED_MIN_GAP,
    );
    this.scriptedSchedule[0].delay = Math.max(STAGE5_WAVES[0].delay, this.scriptedSchedule[0].delay);
    this.nextInfiniteInterval = this.pickNextInfiniteInterval();
  }

  // ── Public ───────────────────────────────────────────────────────────

  update(delta: number, playerReputation = 0, prophecyActive = false, buildScore = 0): void {
    this.buildScore = buildScore;
    this.elapsed += delta;

    // Very early probes: weak scouts that make the enemy presence readable
    // without starting the real wave ramp or threatening a quick loss.
    while (
      this.probeIndex < this.probeSchedule.length &&
      this.elapsed >= this.probeSchedule[this.probeIndex].delay
    ) {
      this.fireWave(this.probeSchedule[this.probeIndex]);
      this.probeIndex++;
    }

    // Let the first tentative attack respond to player momentum. Starting
    // REP is below 90, so this usually fires after the player explores, moves
    // the band, or begins claiming venues. The 120s delay remains a fallback.
    if (
      this.waveIndex === 0 &&
      playerReputation >= FIRST_WAVE_REPUTATION_TRIGGER
    ) {
      this.fireWave(this.scriptedSchedule[0]);
      this.waveIndex++;
      this.lastInfiniteWaveTime = this.elapsed;
    }

    // ── Prophecy warnings: fire ~25s before each upcoming scripted wave ───
    // Wave 0 is rep-triggered so its timing is indeterminate; skip it.
    if (prophecyActive &&
        this.waveIndex >= 1 &&
        this.waveIndex < this.scriptedSchedule.length &&
        this.waveIndex > this.warnedWaveIndex) {
      const nextWave = this.scriptedSchedule[this.waveIndex];
      if (this.elapsed >= nextWave.delay - PROPHECY_WARNING_SECS) {
        this.warnedWaveIndex = this.waveIndex;
        const edges   = edgeSummaryForWave(nextWave);
        const secsLeft = Math.max(5, Math.round(nextWave.delay - this.elapsed));
        this.pendingEnemyActivity.push({
          message:  `◈ Prophecy: Wave ${this.waveIndex + 1} inbound from ${edges} in ~${secsLeft}s`,
          severity: nextWave.enemies.length >= 6 ? 'warning' : 'info',
        });
      }
    }

    // ── Onboarding waves (hardcoded ramp) ────────────────────────────────
    while (
      this.waveIndex < STAGE5_WAVES.length &&
      this.elapsed >= this.scriptedSchedule[this.waveIndex].delay
    ) {
      this.fireWave(this.scriptedSchedule[this.waveIndex]);
      this.waveIndex++;
      // Anchor the infinite-wave clock to the last onboarding wave so the
      // first infinite wave arrives WAVE_INFINITE_INTERVAL seconds later.
      this.lastInfiniteWaveTime = this.elapsed;
    }

    // ── Infinite escalating waves (post-onboarding) ───────────────────────
    if (this.waveIndex >= STAGE5_WAVES.length) {
      // Prophecy warning: fire ~25s before the next infinite wave arrives.
      if (prophecyActive && !this.infiniteWaveWarned) {
        const timeToNext = this.nextInfiniteInterval - (this.elapsed - this.lastInfiniteWaveTime);
        if (timeToNext <= PROPHECY_WARNING_SECS && timeToNext > 0) {
          this.infiniteWaveWarned = true;
          const secs = Math.max(5, Math.round(timeToNext));
          this.pendingEnemyActivity.push({
            message:  `◈ Prophecy: escalating surge inbound in ~${secs}s — fortify the Beacon`,
            severity: 'warning',
          });
        }
      }

      if (this.elapsed - this.lastInfiniteWaveTime >= this.nextInfiniteInterval) {
        this.fireInfiniteWave();
        this.infiniteWaveCount++;
        this.lastInfiniteWaveTime = this.elapsed;
        this.waveIndex++; // advance index so the HUD/toast counter keeps climbing
        this.infiniteWaveWarned = false; // arm warning for the next cycle
        this.nextInfiniteInterval = this.pickNextInfiniteInterval();
      }
    }
  }

  get currentWaveIndex(): number { return this.waveIndex; }

  /**
   * Spawn a single enemy unit at the given edge on behalf of the Codex BattlePlanner.
   * Called when processing EnemyCommand { type: 'spawn' } from BattlePlanner.decide().
   */
  spawnForCodex(enemyType: EnemyType, fromEdge: SpawnEdge): void {
    const pos = this.pickEdgeTile(fromEdge);
    if (pos) this.spawnEnemyUnit(enemyType, pos);
  }

  /** Spawn a unit from a weighted random fog edge, avoiding recent/patrolled edges where possible. */
  spawnFromFog(enemyType: EnemyType, relativeHint?: SpawnEdge): Unit | null {
    const hint = relativeHint ?? SPAWN_EDGES[this.spawnSeed % SPAWN_EDGES.length];
    const edge = this.pickWeightedEdge(hint);
    const pos = this.pickEdgeTile(edge);
    if (!pos) return null;
    this.lastAttackEdge = edge;
    return this.spawnEnemyUnit(enemyType, pos);
  }

  /** Spawn a manufactured unit from the hidden Corporate HQ estate. */
  spawnFromCorporateHQ(enemyType: EnemyType): Unit | null {
    const tiles = this.map.corporateHQ.spawnTiles;
    if (tiles.length === 0) return this.spawnFromFog(enemyType, 'east');
    for (let i = 0; i < tiles.length; i++) {
      const pos = tiles[(this.spawnSeed++) % tiles.length];
      if (this.isReachablePlayable(pos) && !this.isOccupied(pos)) return this.spawnEnemyUnit(enemyType, pos);
    }
    return this.spawnFromFog(enemyType, 'east');
  }

  /** Prophecy Reveal spell — human-readable description of the next incoming wave. */
  nextWavePreview(): string {
    if (this.waveIndex < STAGE5_WAVES.length) {
      const wave = this.scriptedSchedule[this.waveIndex];
      const secs = Math.max(0, Math.round(wave.delay - this.elapsed));
      const edges = edgeSummaryForWave(wave);
      return `Wave ${this.waveIndex + 1} from ${edges} in ~${secs}s — ${wave.enemies.length} threats`;
    }
    const timeToNext = this.nextInfiniteInterval - (this.elapsed - this.lastInfiniteWaveTime);
    if (timeToNext > 0) {
      const secs = Math.max(0, Math.round(timeToNext));
      return `escalating surge from multiple edges in ~${secs}s`;
    }
    return 'surge imminent — defend the Beacon now';
  }

  /** Snapshot for save/load. */
  getState() {
    return {
      elapsed:              this.elapsed,
      waveIndex:            this.waveIndex,
      infiniteWaveCount:    this.infiniteWaveCount,
      lastInfiniteWaveTime: this.lastInfiniteWaveTime,
      probeIndex:           this.probeIndex,
      lastAttackEdge:       this.lastAttackEdge,
      introducedEnemyTypes: Array.from(this.introducedEnemyTypes),
      warnedWaveIndex:      this.warnedWaveIndex,
      infiniteWaveWarned:   this.infiniteWaveWarned,
      probeSchedule:        this.probeSchedule,
      scriptedSchedule:     this.scriptedSchedule,
      nextInfiniteInterval: this.nextInfiniteInterval,
    };
  }

  /** Restore wave state from a save snapshot. */
  restoreState(s: ReturnType<WaveSystem['getState']>): void {
    this.elapsed              = s.elapsed;
    this.waveIndex            = s.waveIndex;
    this.infiniteWaveCount    = s.infiniteWaveCount;
    this.lastInfiniteWaveTime = s.lastInfiniteWaveTime;
    this.probeIndex           = s.probeIndex ?? EARLY_PROBES.length;
    this.lastAttackEdge       = s.lastAttackEdge ?? null;
    this.warnedWaveIndex      = (s as any).warnedWaveIndex ?? Math.max(0, s.waveIndex - 1);
    this.infiniteWaveWarned   = (s as any).infiniteWaveWarned ?? false;
    this.probeSchedule        = (s as any).probeSchedule ?? this.probeSchedule;
    this.scriptedSchedule     = (s as any).scriptedSchedule ?? this.scriptedSchedule;
    this.nextInfiniteInterval = (s as any).nextInfiniteInterval ?? this.pickNextInfiniteInterval();
    this.introducedEnemyTypes.clear();
    for (const key of s.introducedEnemyTypes ?? []) {
      this.introducedEnemyTypes.add(key);
    }
    this.pendingEnemyIntroductions.length = 0;
  }

  /**
   * Called by Game.ts after a Song Drop — Industry retaliates by accelerating
   * the next infinite wave by ~35 seconds (but never firing it immediately).
   * Only has effect during the infinite wave phase.
   */
  triggerRage(): void {
    if (this.waveIndex < STAGE5_WAVES.length) return;
    const RAGE_ACCELERATION = 35; // seconds shaved from next wave timer
    this.lastInfiniteWaveTime -= RAGE_ACCELERATION;
    this.infiniteWaveWarned = false; // re-arm Prophecy warning for the accelerated wave
  }

  // ── Internals ─────────────────────────────────────────────────────────

  private fireWave(wave: WaveSpec): void {
    const enemies = this.applyAdaptivePressure(wave.enemies);
    const edgeMap = new Map<SpawnEdge, SpawnEdge>();
    const usedEdges = new Set<SpawnEdge>();
    let spawned = 0;
    for (const spec of enemies) {
      let edge = edgeMap.get(spec.edge);
      if (!edge) {
        edge = this.pickWeightedEdge(spec.edge, usedEdges);
        edgeMap.set(spec.edge, edge);
        usedEdges.add(edge);
      }
      const pos = this.pickEdgeTile(edge);
      if (!pos) continue;
      this.spawnEnemyUnit(spec.type, pos, spec.variant ?? 'normal');
      spawned++;
    }
    this.lastAttackEdge = edgeMap.values().next().value ?? this.lastAttackEdge;
    if (wave.notice && spawned > 0) {
      this.pendingEnemyActivity.push({
        message: wave.notice,
        severity: spawned >= 3 ? 'warning' : 'info',
      });
    }
  }

  /**
   * Send a procedurally-generated infinite wave. Enemy count and composition
   * scale with `infiniteWaveCount` — harder every time.
   *
   * Tier ladder (4 waves per tier):
   *   Tier 0 — Swarm + Mogul
   *   Tier 1 — Swarm + Mogul + BadManager + FilePirate
   *   Tier 2 — + TributeAct
   *   Tier 3+ — TributeAct-heavy, all types
   */
  private fireInfiniteWave(): void {
    const tier = Math.floor(this.infiniteWaveCount / 4);
    // Extra enemies from build score only kick in after the grace period,
    // so players can invest in building before the walls close in.
    const buildBonus = this.infiniteWaveCount >= BUILD_SCORE_ESCALATION_DELAY
      ? Math.min(Math.floor(this.buildScore / BUILD_SCORE_ESCALATION_DIV), BUILD_SCORE_ESCALATION_CAP)
      : 0;
    // Count grows gently: 3, 4, 5, 6, … (cap at 16 for playability)
    const pressureShift = this.playerPressureShift();
    const count = Math.min(Math.max(2, 3 + this.infiniteWaveCount + buildBonus + pressureShift), 16);
    const edges = this.pickWeightedEdgeCycle();

    for (let i = 0; i < count; i++) {
      const edge = edges[i % edges.length];
      const type = this.pickEnemyForTier(tier, i);
      const pos  = this.pickEdgeTile(edge);
      if (!pos) continue;
      const variant = tier >= 1 && i === Math.min(count - 1, 3) ? 'signalJammer' : 'normal';
      this.spawnEnemyUnit(type, pos, variant);
    }
    this.lastAttackEdge = edges[0] ?? this.lastAttackEdge;
  }

  /**
   * Choose an enemy type based on the current difficulty tier and slot index.
   * Later slots in the same wave tend to be lighter units so the wave has
   * a "heavies in front, swarm behind" feel.
   */
  private pickEnemyForTier(tier: number, slot: number): EnemyType {
    if (slot > 0 && Math.random() < 0.18 + Math.min(0.12, tier * 0.03)) {
      return weightedEnemyPick(enemyPoolForTier(tier));
    }
    if (tier === 0) {
      return slot % 3 === 0 ? 'StreamingMogul' : 'GenZSwarm';
    }
    if (tier === 1) {
      const pick = slot % 5;
      if (pick === 0) return 'StreamingMogul';
      if (pick === 1) return 'BadManager';
      if (pick === 2) return 'FilePirate';
      return 'GenZSwarm';
    }
    if (tier === 2) {
      const pick = slot % 6;
      if (pick === 0) return 'TributeAct';
      if (pick === 1) return 'StreamingMogul';
      if (pick === 2) return 'BadManager';
      if (pick === 3) return 'FilePirate';
      return 'GenZSwarm';
    }
    // Tier 3+: heavy-dominant
    const pick = slot % 6;
    if (pick === 0 || pick === 1) return 'TributeAct';
    if (pick === 2) return 'StreamingMogul';
    if (pick === 3) return 'BadManager';
    if (pick === 4) return 'FilePirate';
    return 'GenZSwarm';
  }

  private pickWeightedEdge(relativeHint: SpawnEdge, usedThisWave = new Set<SpawnEdge>()): SpawnEdge {
    const candidates = SPAWN_EDGES.filter(edge =>
      this.edgeTiles[edge].length > 0 &&
      !usedThisWave.has(edge) &&
      edge !== this.lastAttackEdge,
    );
    const fallback = SPAWN_EDGES.filter(edge =>
      this.edgeTiles[edge].length > 0 &&
      !usedThisWave.has(edge),
    );
    const pool = candidates.length > 0 ? candidates : fallback;
    if (pool.length === 0) return relativeHint;

    const scored = pool.map(edge => ({
      edge,
      weight: this.edgeWeight(edge, relativeHint),
    }));
    const total = scored.reduce((sum, item) => sum + item.weight, 0);
    if (total <= 0) return scored[0].edge;

    let roll = Math.random() * total;
    for (const item of scored) {
      roll -= item.weight;
      if (roll <= 0) return item.edge;
    }
    return scored[scored.length - 1].edge;
  }

  private pickWeightedEdgeCycle(): SpawnEdge[] {
    const result: SpawnEdge[] = [];
    const used = new Set<SpawnEdge>();
    for (let i = 0; i < SPAWN_EDGES.length; i++) {
      const hint = SPAWN_EDGES[(i + this.infiniteWaveCount) % SPAWN_EDGES.length];
      const edge = this.pickWeightedEdge(hint, used);
      if (!used.has(edge)) {
        result.push(edge);
        used.add(edge);
      }
    }
    for (const edge of SPAWN_EDGES) {
      if (!used.has(edge) && this.edgeTiles[edge].length > 0) result.push(edge);
    }
    return result.length > 0 ? result : [...SPAWN_EDGES];
  }

  private edgeWeight(edge: SpawnEdge, relativeHint: SpawnEdge): number {
    const distance = this.distanceFromPlayerArmy(edge);
    let weight = Math.max(0.1, distance / 10);
    if (edge === relativeHint) weight *= 1.2;
    if (distance < EDGE_TOO_CLOSE_TO_ARMY) weight *= 0.08;
    if (edge === this.lastAttackEdge) weight *= 0.02;
    return weight;
  }

  private pickNextInfiniteInterval(): number {
    const jitter = randomInt(-INFINITE_INTERVAL_JITTER, INFINITE_INTERVAL_JITTER);
    const pressureShift = this.playerPressureShift();
    const adaptiveShift = pressureShift > 0 ? -8 : pressureShift < 0 ? 12 : 0;
    return Math.max(45, WAVE_INFINITE_INTERVAL + jitter + adaptiveShift);
  }

  private playerPressureShift(): number {
    const activeGoodies = this.units.filter(unit =>
      unit.alive &&
      !unit.isDowned &&
      unit.faction === Faction.Player &&
      unit.bandClass !== 'source',
    );
    const activeBand = activeGoodies.filter(unit => unit.bandClass !== 'fan').length;
    const fans = activeGoodies.filter(unit => unit.bandClass === 'fan').length;

    if (activeBand <= 2 && fans <= 1) return -2;
    if (activeBand <= 3 && fans <= 2) return -1;
    if (activeBand >= 5 && fans >= 8) return 2;
    if (activeBand >= 4 && fans >= 5) return 1;
    return 0;
  }

  private applyAdaptivePressure(enemies: EnemySpec[]): EnemySpec[] {
    const pressureShift = this.playerPressureShift();
    const result = [...enemies];

    if (pressureShift < 0 && result.length >= 5) {
      const removable = result
        .map((spec, index) => ({ spec, index }))
        .filter(({ spec }) => !spec.variant || spec.variant === 'normal' || spec.variant === 'monolithGuard');
      const removeCount = Math.min(Math.abs(pressureShift), Math.max(0, removable.length - 2));
      for (let i = 0; i < removeCount; i++) {
        const pick = removable.splice(Math.floor(Math.random() * removable.length), 1)[0];
        if (!pick) continue;
        const idx = result.indexOf(pick.spec);
        if (idx >= 0) result.splice(idx, 1);
      }
    }

    if (pressureShift > 0) {
      const extraCount = Math.min(pressureShift, 2);
      for (let i = 0; i < extraCount; i++) {
        result.push({
          type: weightedEnemyPick([
            ['GenZSwarm', 5],
            ['FilePirate', 3],
            ['BadManager', 2],
          ]),
          edge: randomEdge(),
        });
      }
    }

    return result;
  }

  private distanceFromPlayerArmy(edge: SpawnEdge): number {
    const players = this.units.filter(unit =>
      unit.alive &&
      !unit.isDowned &&
      unit.faction === Faction.Player,
    );
    if (players.length === 0) return 999;

    const tiles = this.edgeTiles[edge];
    if (tiles.length === 0) return 0;

    let best = Infinity;
    const stride = Math.max(1, Math.floor(tiles.length / 12));
    for (let i = 0; i < tiles.length; i += stride) {
      const tile = tiles[i];
      for (const player of players) {
        const d = Math.hypot(player.gridPos.col - tile.col, player.gridPos.row - tile.row);
        if (d < best) best = d;
      }
    }
    return Number.isFinite(best) ? best : 999;
  }

  /** Shared unit-creation helper used by both onboarding and infinite waves. */
  private spawnEnemyUnit(
    type: EnemyType,
    pos: { col: number; row: number },
    variant: EnemyVariant = 'normal',
  ): Unit {
    const isStreamingMonolith = variant === 'streamingMonolith';
    const isMonolithGuard = variant === 'monolithGuard';
    const isSignalJammer = variant === 'signalJammer';
    const isEarlyScout = variant === 'earlyScout';
    const isFilePirate = type === 'FilePirate';
    const unit             = new Unit();
    unit.gridPos           = { ...pos };
    unit.enemyType         = type;
    unit.faction           = type === 'MusicPress' ? Faction.Neutral : Faction.Enemy;
    unit.displayName       = isStreamingMonolith ? 'Streaming Monolith' : isMonolithGuard ? 'Monolith Guard' : isSignalJammer ? 'Signal Jammer' : isEarlyScout ? 'A&R' : displayNameFor(type);
    unit.threatScale       = isStreamingMonolith ? 2.35 : isMonolithGuard ? 1.12 : isSignalJammer ? 0.95 : isEarlyScout ? 0.9 : isFilePirate ? 1.08 : type === 'RivalVenueBuilder' ? 1.05 : 1;
    unit.specialRole       = isStreamingMonolith ? 'streamingMonolith' : type === 'RivalVenueBuilder' ? 'rivalVenueBuilder' : null;
    unit.objectiveTarget   = isStreamingMonolith ? this.pickBroadcastSite(pos) : null;
    unit.maxHp             = Math.round(hpFor(type, this.balance) * (isStreamingMonolith ? 7 : isMonolithGuard ? 1.55 : isSignalJammer ? 0.7 : isEarlyScout ? 0.75 : 1));
    unit.hp                = unit.maxHp;
    unit.moveSpeed         = moveSpeedFor(type, this.balance) * (isStreamingMonolith ? 0.68 : isMonolithGuard ? 1.08 : isSignalJammer ? 0.82 : isEarlyScout ? 1.15 : 1);
    unit.attackDamage      = type === 'StreamingMogul' && !isStreamingMonolith && !isMonolithGuard
      ? 0
      : Math.round(damageFor(type, this.balance) * (isStreamingMonolith ? 2.4 : isMonolithGuard ? 1.25 : isSignalJammer ? 0.45 : isEarlyScout ? 0.65 : 1));
    unit.attackRate        = this.balance.enemyAttackRate * (isStreamingMonolith ? 0.72 : isSignalJammer ? 0.65 : isEarlyScout ? 0.95 : isFilePirate ? 0.55 : type === 'RivalVenueBuilder' ? 0.1 : 1);
    unit.sightRadius       = this.balance.enemySightRadius + (isStreamingMonolith ? 4 : isMonolithGuard ? 1 : isSignalJammer ? 3 : isEarlyScout ? 1 : isFilePirate ? 6 : 0);
    unit.attackRadius      = this.balance.enemyAttackRadius + (isStreamingMonolith ? 0.5 : isSignalJammer ? 1.25 : isFilePirate ? 7 : 0);
    unit.pressHostileTimer = type === 'MusicPress' ? MUSIC_PRESS_HOSTILE_TIME : null;
    // ── Unit-type initial AI state ────────────────────────────────────────
    // StreamingMogul: stagger first aura by half the interval so it doesn't
    // fire the very first frame; subsequent pulses use the full interval.
    if (type === 'StreamingMogul') unit.auraTimer = MOGUL_AURA_INTERVAL * 0.5;
    // GenZSwarm: initialise burst cycle so the first sprint fires after one full interval.
    if (type === 'GenZSwarm') unit.burstCycleTimer = GENZ_BURST_INTERVAL;

    this.units.push(unit);
    this.renderer.add(unit);

    const introKey = enemyIntroductionKey(type, variant);
    if (!this.introducedEnemyTypes.has(introKey)) {
      this.introducedEnemyTypes.add(introKey);
      this.pendingEnemyIntroductions.push(enemyIntroductionFor(type, variant));
    }

    return unit;
  }

  /**
   * Round-robin through pre-cached passable tiles so consecutive enemies
   * don't all spawn on the same tile.
   */
  private pickEdgeTile(edge: SpawnEdge): { col: number; row: number } | null {
    const list = this.edgeTiles[edge];
    if (list.length === 0) return null;

    for (let i = 0; i < list.length; i++) {
      const pos = list[(this.spawnSeed++) % list.length];
      if (!this.isOccupied(pos)) return pos;
    }

    return null;
  }

  private isOccupied(pos: { col: number; row: number }): boolean {
    return this.units.some((unit) =>
      unit.alive &&
      unit.gridPos.col === pos.col &&
      unit.gridPos.row === pos.row,
    );
  }

  /**
   * For each position along each edge, scan inward up to EDGE_DEPTH tiles and
   * record the FIRST passable (grass / path) tile found.
   * This gives one spawn candidate per column / row — enemies always enter from
   * the outermost reachable position, not from the map border (which is locked).
   */
  private buildEdgeTiles(): void {
    const { width, height } = this.map;
    // The expanded map can have large fog margins. Scan deeply enough to find
    // the outer rim of the main playable landmass, while isValidEdgeSpawn()
    // still keeps generic waves off the secret Corporate HQ island.
    const D = Math.max(width, height);

    for (let col = 2; col < width - 2; col++) {
      // North — scan top-down, keep first passable
      for (let row = 0; row < D; row++) {
        const pos = { col, row };
        if (this.isValidEdgeSpawn(pos)) { this.edgeTiles.north.push(pos); break; }
      }
      // South — scan bottom-up, keep first passable
      for (let row = height - 1; row >= height - D; row--) {
        const pos = { col, row };
        if (this.isValidEdgeSpawn(pos)) { this.edgeTiles.south.push(pos); break; }
      }
    }
    for (let row = 2; row < height - 2; row++) {
      // West — scan left-to-right, keep first passable
      for (let col = 0; col < D; col++) {
        const pos = { col, row };
        if (this.isValidEdgeSpawn(pos)) { this.edgeTiles.west.push(pos); break; }
      }
      // East — scan right-to-left, keep first passable
      for (let col = width - 1; col >= width - D; col--) {
        const pos = { col, row };
        if (this.isValidEdgeSpawn(pos)) { this.edgeTiles.east.push(pos); break; }
      }
    }
  }

  private isValidEdgeSpawn(pos: { col: number; row: number }): boolean {
    if (!this.map.isPassable(pos)) return false;
    if (!this.isReachablePlayable(pos)) return false;
    const hq = this.map.corporateHQ;
    const nearHQ = Math.hypot(pos.col - hq.buildingCol, pos.row - hq.buildingRow) <= CORPORATE_HQ_EDGE_SPAWN_EXCLUSION_RADIUS;
    const corporateApproach = pos.col >= hq.accessCol - 16 && pos.row <= hq.accessRow + 45;
    return !nearHQ && !corporateApproach;
  }

  private buildMainReachableTiles(): void {
    this.mainReachableTiles.clear();
    const seeds = this.findMainReachabilitySeeds();
    const queue: GridPosition[] = [...seeds];

    for (const seed of seeds) {
      this.mainReachableTiles.add(this.tileKey(seed));
    }

    for (let i = 0; i < queue.length; i++) {
      const current = queue[i];
      for (const next of this.neighbourTiles(current)) {
        const key = this.tileKey(next);
        if (this.mainReachableTiles.has(key)) continue;
        if (!this.map.inBounds(next.col, next.row) || !this.map.isPassable(next)) continue;
        this.mainReachableTiles.add(key);
        queue.push(next);
      }
    }
  }

  private findMainReachabilitySeeds(): GridPosition[] {
    const centre = {
      col: this.map.beacon.col,
      row: this.map.beacon.row,
    };
    const seeds: GridPosition[] = [];
    for (let radius = 1; radius <= 10 && seeds.length === 0; radius++) {
      for (let dc = -radius; dc <= radius; dc++) {
        for (let dr = -radius; dr <= radius; dr++) {
          const pos = { col: centre.col + dc, row: centre.row + dr };
          if (!this.map.inBounds(pos.col, pos.row) || !this.map.isPassable(pos)) continue;
          seeds.push(pos);
        }
      }
    }
    return seeds;
  }

  private neighbourTiles(pos: GridPosition): GridPosition[] {
    return [
      { col: pos.col + 1, row: pos.row },
      { col: pos.col - 1, row: pos.row },
      { col: pos.col,     row: pos.row + 1 },
      { col: pos.col,     row: pos.row - 1 },
      { col: pos.col + 1, row: pos.row + 1 },
      { col: pos.col + 1, row: pos.row - 1 },
      { col: pos.col - 1, row: pos.row + 1 },
      { col: pos.col - 1, row: pos.row - 1 },
    ];
  }

  private isReachablePlayable(pos: GridPosition): boolean {
    return this.mainReachableTiles.has(this.tileKey(pos));
  }

  private tileKey(pos: GridPosition): string {
    return `${pos.col},${pos.row}`;
  }

  private pickBroadcastSite(from: { col: number; row: number }): { col: number; row: number } {
    const centre = {
      col: Math.floor(this.map.width / 2),
      row: Math.floor(this.map.height / 2),
    };
    const candidates = [
      { col: centre.col,     row: centre.row - 10 },
      { col: centre.col + 8, row: centre.row - 6  },
      { col: centre.col - 8, row: centre.row - 6  },
      { col: centre.col + 9, row: centre.row + 5  },
      { col: centre.col - 9, row: centre.row + 5  },
    ].filter(pos => this.map.inBounds(pos.col, pos.row) && this.map.isPassable(pos));

    candidates.sort((a, b) => {
      const da = Math.hypot(from.col - a.col, from.row - a.row);
      const db = Math.hypot(from.col - b.col, from.row - b.row);
      return da - db;
    });

    return candidates[0] ?? centre;
  }
}

function edgeSummaryForWave(wave: WaveSpec): string {
  const seen = new Set<SpawnEdge>();
  for (const e of wave.enemies) seen.add(e.edge);
  return [...seen].map(e => e.charAt(0).toUpperCase() + e.slice(1)).join(' + ');
}

function randomiseWaveSchedule(
  waves: WaveSpec[],
  jitterBefore: number,
  jitterAfter: number,
  minGap: number,
): WaveSpec[] {
  let previousDelay = 0;
  return waves.map((wave) => {
    const delay = Math.max(
      previousDelay + minGap,
      wave.delay + randomInt(-jitterBefore, jitterAfter),
    );
    previousDelay = delay;
    return {
      ...wave,
      delay,
      enemies: wave.enemies.map(enemy => ({ ...enemy })),
    };
  });
}

function varyScriptedWave(wave: WaveSpec, index: number): WaveSpec {
  const enemies = wave.enemies.map(enemy => ({
    ...enemy,
    edge: shouldPreserveEdge(enemy) || Math.random() > 0.42 ? enemy.edge : randomEdge(),
  }));

  if (index === 0) {
    if (Math.random() < 0.35) enemies.push({ type: 'GenZSwarm', edge: randomEdge() });
    return { ...wave, enemies };
  }

  if (index === 2) {
    const guardIndexes = enemies
      .map((enemy, guardIndex) => ({ enemy, guardIndex }))
      .filter(({ enemy }) => enemy.variant === 'monolithGuard');
    const guardsToTrim = randomInt(0, 2);
    for (let i = 0; i < guardsToTrim; i++) {
      const pick = guardIndexes.splice(Math.floor(Math.random() * guardIndexes.length), 1)[0];
      if (!pick) continue;
      const idx = enemies.indexOf(pick.enemy);
      if (idx >= 0) enemies.splice(idx, 1);
    }
    if (Math.random() < 0.6) enemies.push({ type: 'FilePirate', edge: randomEdge() });
    if (Math.random() < 0.45) enemies.push({ type: 'BadManager', edge: randomEdge(), variant: 'signalJammer' });
    return { ...wave, enemies };
  }

  const extras = randomInt(0, Math.min(2, index));
  for (let i = 0; i < extras; i++) {
    enemies.push({
      type: weightedEnemyPick([
        ['GenZSwarm', 5],
        ['FilePirate', index >= 1 ? 3 : 0],
        ['BadManager', index >= 2 ? 2 : 1],
        ['StreamingMogul', index >= 3 ? 2 : 0],
        ['TributeAct', index >= 4 ? 1 : 0],
        ['RivalVenueBuilder', index >= 3 ? 1 : 0],
      ]),
      edge: randomEdge(),
    });
  }

  if (enemies.length > 5 && Math.random() < 0.28) {
    const normalIndexes = enemies
      .map((enemy, enemyIndex) => ({ enemy, enemyIndex }))
      .filter(({ enemy }) => !enemy.variant || enemy.variant === 'normal');
    const pick = normalIndexes[Math.floor(Math.random() * normalIndexes.length)];
    if (pick) enemies.splice(pick.enemyIndex, 1);
  }

  return { ...wave, enemies };
}

function shouldPreserveEdge(enemy: EnemySpec): boolean {
  return enemy.variant === 'streamingMonolith';
}

function enemyPoolForTier(tier: number): Array<[EnemyType, number]> {
  if (tier <= 0) {
    return [
      ['GenZSwarm', 6],
      ['StreamingMogul', 2],
      ['FilePirate', 1],
    ];
  }
  if (tier === 1) {
    return [
      ['GenZSwarm', 5],
      ['StreamingMogul', 3],
      ['BadManager', 3],
      ['FilePirate', 3],
      ['RivalVenueBuilder', 1],
    ];
  }
  if (tier === 2) {
    return [
      ['GenZSwarm', 4],
      ['StreamingMogul', 3],
      ['BadManager', 3],
      ['FilePirate', 3],
      ['TributeAct', 2],
      ['RivalVenueBuilder', 2],
    ];
  }
  return [
    ['GenZSwarm', 3],
    ['StreamingMogul', 3],
    ['BadManager', 3],
    ['FilePirate', 3],
    ['TributeAct', 4],
    ['RivalVenueBuilder', 3],
  ];
}

function weightedEnemyPick(pool: Array<[EnemyType, number]>): EnemyType {
  const filtered = pool.filter(([, weight]) => weight > 0);
  const total = filtered.reduce((sum, [, weight]) => sum + weight, 0);
  let roll = Math.random() * total;
  for (const [type, weight] of filtered) {
    roll -= weight;
    if (roll <= 0) return type;
  }
  return filtered[filtered.length - 1]?.[0] ?? 'GenZSwarm';
}

function randomEdge(): SpawnEdge {
  return SPAWN_EDGES[Math.floor(Math.random() * SPAWN_EDGES.length)];
}

function randomInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function displayNameFor(type: EnemyType): string {
  switch (type) {
    case 'StreamingMogul': return 'Streaming Mogul';
    case 'GenZSwarm':      return 'GenZ Swarm';
    case 'BadManager':     return 'Bad Manager';
    case 'TributeAct':     return 'Corporate Tribute Act';
    case 'MusicPress':     return 'Music Press';
    case 'FilePirate':     return 'File Sharing Pirate';
    case 'RivalVenueBuilder': return 'Rival Venue Builder';
  }
}

function enemyIntroductionKey(type: EnemyType, variant: EnemyVariant): string {
  return variant === 'normal' || variant === 'monolithGuard' ? type : `${type}:${variant}`;
}

function enemyIntroductionFor(type: EnemyType, variant: EnemyVariant = 'normal'): EnemyIntroduction {
  if (variant === 'earlyScout') {
    return {
      type,
      key: enemyIntroductionKey(type, variant),
      title: 'A&R',
      disruption: 'early talent-spotters testing your routes before real waves arrive',
      severity: 'info',
    };
  }
  if (variant === 'streamingMonolith') {
    return {
      type,
      key: enemyIntroductionKey(type, variant),
      title: 'Streaming Monolith',
      disruption: 'building a rival Beacon; smash it before broadcast',
      severity: 'danger',
    };
  }
  if (variant === 'signalJammer') {
    return {
      type,
      key: enemyIntroductionKey(type, variant),
      title: 'Signal Jammer',
      disruption: 'fragile support unit muting future-song healing',
      severity: 'danger',
    };
  }

  switch (type) {
    case 'GenZSwarm':
      return {
        type,
        key: enemyIntroductionKey(type, variant),
        title: 'GenZ Swarm',
        disruption: 'fast raiders hunting fans and loose economy',
        severity: 'warning',
      };
    case 'MusicPress':
      return {
        type,
        key: enemyIntroductionKey(type, variant),
        title: 'Music Press',
        disruption: 'send Ali to convert them before the bad review lands',
        severity: 'info',
      };
    case 'StreamingMogul':
      return {
        type,
        key: enemyIntroductionKey(type, variant),
        title: 'Streaming Mogul',
        disruption: 'passively draining Beacon signal and Sales',
        severity: 'warning',
      };
    case 'BadManager':
      return {
        type,
        key: enemyIntroductionKey(type, variant),
        title: 'Bad Manager',
        disruption: 'hunting Ali — Keyboard Prophets can expose them',
        severity: 'danger',
      };
    case 'TributeAct':
      return {
        type,
        key: enemyIntroductionKey(type, variant),
        title: 'Corporate Tribute Act',
        disruption: 'heavy bruiser built to break defences',
        severity: 'danger',
      };
    case 'FilePirate':
      return {
        type,
        key: enemyIntroductionKey(type, variant),
        title: 'File Sharing Pirate',
        disruption: 'ranged torrent attacks against venues and isolated fans',
        severity: 'warning',
      };
    case 'RivalVenueBuilder':
      return {
        type,
        key: enemyIntroductionKey(type, variant),
        title: 'Rival Venue Builder',
        disruption: 'builds rival venues that level up and spawn Industry crews until destroyed',
        severity: 'danger',
      };
  }
}

function hpFor(type: EnemyType, b: BalanceValues): number {
  switch (type) {
    case 'StreamingMogul': return b.enemyHpMogul;
    case 'GenZSwarm':      return b.enemyHpSwarm;
    case 'BadManager':     return b.enemyHpBadManager;
    case 'TributeAct':     return b.enemyHpTribute;
    case 'MusicPress':     return b.enemyHpMusicPress;
    case 'FilePirate':     return b.enemyHpPirate;
    case 'RivalVenueBuilder': return ENEMY_HP_RIVAL_BUILDER;
  }
}

function damageFor(type: EnemyType, b: BalanceValues): number {
  switch (type) {
    case 'StreamingMogul': return b.enemyDamageMogul;
    case 'GenZSwarm':      return b.enemyDamageSwarm;
    case 'BadManager':     return b.enemyDamageBadManager;
    case 'TributeAct':     return b.enemyDamageTribute;
    case 'MusicPress':     return b.enemyDamageMusicPress;
    case 'FilePirate':     return b.enemyDamagePirate;
    case 'RivalVenueBuilder': return ENEMY_DAMAGE_RIVAL_BUILDER;
  }
}

function moveSpeedFor(type: EnemyType, b: BalanceValues): number {
  switch (type) {
    case 'StreamingMogul': return b.enemyMoveSpeedMogul;
    case 'GenZSwarm':      return b.enemyMoveSpeedSwarm;
    case 'BadManager':     return b.enemyMoveSpeedBadManager;
    case 'TributeAct':     return b.enemyMoveSpeedTribute;
    case 'MusicPress':     return b.enemyMoveSpeedMusicPress;
    case 'FilePirate':     return b.enemyMoveSpeedPirate;
    case 'RivalVenueBuilder': return ENEMY_MOVE_SPEED_RIVAL_BUILDER;
  }
}
